import { ref, computed, watch } from "vue";
import { useCartStore } from "~/stores/cart";
import { useToast } from "~/composables/useToast";
import { useTracking, buildGaItem } from "~/composables/useTracking";
import { readVariantQty } from "./variantDisplay";

/* ====================================================================
 *  useProductCart
 *  – відстежує залишок / кількість у кошику
 *  – addToCart з GA4 + Meta Pixel подіями
 * ==================================================================== */
export function useProductCart({
  product,
  currentVariant,
  selectedColor,
  selectedSize,
  matchingVariants,
  gallery,
  priceToShow,
  inStock,
  variantSku,
  mustChooseOptions,
}) {
  const cart = useCartStore();
  const toast = useToast();
  const { ga: gaLog, pixel: mpTrack } = useTracking();

  const qty = ref(1);

  /* ---- Stock tracking ---- */
  const cartKeyForCurrent = computed(() => {
    const p = product.value || {};
    const v = currentVariant.value;
    if (!p?.id) return "";
    if (v?.id) return [String(p.id), String(v.id)].join("|");
    return [
      String(p.id),
      String(selectedColor.value || ""),
      String(selectedSize.value || ""),
    ].join("|");
  });

  const alreadyInCartQty = computed(() => {
    const key = cartKeyForCurrent.value;
    if (!key) return 0;
    const hit = (cart.items || []).find((i) => String(i.key) === String(key));
    return hit ? Math.max(0, Number(hit.qty || 0)) : 0;
  });

  const availableQty = computed(() => {
    const v = currentVariant.value;
    // Не `v.stock`: на SSR варіант приходить із `variantsLite`, а там
    // залишок зветься `stockQty` — інакше стеля кількості впала б у 0
    // і кнопка «додати» була б мертвою до гідрації (п.9г).
    return v ? Math.max(0, readVariantQty(v) ?? 0) : 0;
  });

  const remainingToAdd = computed(() =>
    Math.max(0, Number(availableQty.value - alreadyInCartQty.value || 0)),
  );

  const maxSelectableQty = computed(() => {
    if (!inStock.value) return 1;
    return Math.max(1, remainingToAdd.value || 0);
  });

  const canAdd = computed(
    () =>
      inStock.value &&
      remainingToAdd.value > 0 &&
      (!mustChooseOptions.value ||
        (selectedColor.value && selectedSize.value)) &&
      qty.value > 0 &&
      qty.value <= maxSelectableQty.value,
  );

  /* ---- Auto-clamp on variant / stock change ---- */
  watch(
    () => [currentVariant.value?.id, remainingToAdd.value],
    () => {
      qty.value = Math.max(
        1,
        Math.min(maxSelectableQty.value, Number(qty.value || 1)),
      );
    },
  );

  /* ---- Qty handlers ---- */
  function onQtyInput(e) {
    const raw = parseInt(e.target.value || "1", 10);
    const v = isNaN(raw) ? 1 : raw;
    if (inStock.value && v > maxSelectableQty.value) {
      toast.show(`Можна додати максимум ${maxSelectableQty.value} шт`, {
        type: "error",
      });
    }
    qty.value = Math.max(1, Math.min(maxSelectableQty.value, v));
  }

  function onIncQty() {
    if (!inStock.value) {
      toast.show("Немає в наявності", { type: "error" });
      return;
    }
    if (qty.value >= maxSelectableQty.value) {
      toast.show(`Можна додати максимум ${maxSelectableQty.value} шт`, {
        type: "error",
      });
      return;
    }
    qty.value = Math.min(maxSelectableQty.value, Number(qty.value || 1) + 1);
  }

  /* ---- Helpers ---- */
  function firstImageUrl(p, v = null) {
    if (Array.isArray(gallery.value) && gallery.value[0])
      return gallery.value[0];
    if (v && Array.isArray(v.images) && v.images[0]) return v.images[0];
    if (Array.isArray(p?.images) && p.images[0]) return p.images[0];
    return "";
  }

  function gaItem(quantity = 1) {
    return buildGaItem({
      product: product.value,
      variant: currentVariant.value,
      variantFallback: [selectedColor.value, selectedSize.value],
      price: Number(priceToShow.value || 0),
      quantity,
    });
  }

  /* ---- Add to cart ---- */
  function addToCart() {
    if (!canAdd.value) return;

    if (remainingToAdd.value <= 0) {
      toast.show("На жаль, більше немає в наявності", { type: "error" });
      return;
    }
    if (Number(qty.value || 0) > remainingToAdd.value) {
      toast.show(`Можна додати максимум ${remainingToAdd.value} шт`, {
        type: "error",
      });
      qty.value = Math.max(1, remainingToAdd.value);
      return;
    }

    const p = product.value || {};
    const v = currentVariant.value;

    if (
      (mustChooseOptions.value || (matchingVariants.value || []).length > 1) &&
      !v?.id
    )
      return;

    const img = firstImageUrl(p, v);

    cart.add({
      productId: p.id,
      variantId: v?.id || "",
      sku: v?.sku || variantSku.value || "",
      slug: p.slug,
      title: p.title,
      price: Number(priceToShow.value || 0),
      image: img,
      color: selectedColor.value || "",
      size: selectedSize.value || "",
      qty: Number(qty.value || 1),
    });

    gaLog("add_to_cart", {
      currency: "UAH",
      value: Number(priceToShow.value || 0) * Number(qty.value || 1),
      items: [gaItem(Number(qty.value || 1))],
    });

    mpTrack("AddToCart", {
      content_type: "product",
      content_ids: [String(p?.id || "")].filter(Boolean),
      content_name: String(p?.title || ""),
      currency: "UAH",
      value: Number(priceToShow.value || 0) * Number(qty.value || 1),
      contents: [
        {
          id: String(p?.id || ""),
          quantity: Number(qty.value || 1),
          item_price: Number(priceToShow.value || 0),
        },
      ].filter((x) => x.id),
    });
  }

  return {
    qty,
    canAdd,
    addToCart,
    onQtyInput,
    onIncQty,
    remainingToAdd,
    maxSelectableQty,
  };
}
